import { useMemo, useState } from "react";
import { View } from "react-native";
import { Button } from "~/components/ui/Button";
import { DateField } from "~/components/ui/DateField";
import { RuleLine } from "~/components/ui/RuleLine";
import { SegmentedControl } from "~/components/ui/SegmentedControl";
import { CaptionText, FigureText } from "~/components/ui/Text";
import { TextField } from "~/components/ui/TextField";
import { spacing, useTheme } from "~/styles/theme";
import {
  computeCounterValue,
  describeCounterValue,
  formatDayCount,
  today,
} from "~/utils/dates";
import type { CounterColor, Direction, NewCounter } from "~/types";

export function CounterForm({
  initial,
  submitLabel,
  onSubmit,
  onDelete,
}: {
  initial?: Partial<NewCounter>;
  submitLabel: string;
  onSubmit: (value: NewCounter) => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const { colors } = useTheme();
  const [label, setLabel] = useState(initial?.label ?? "");
  const [targetDate, setTargetDate] = useState(initial?.targetDate ?? today());
  const [direction, setDirection] = useState<Direction>(
    initial?.direction ?? "up",
  );
  const [color, setColor] = useState<CounterColor>(initial?.color ?? "ink");
  const [submitting, setSubmitting] = useState(false);

  const preview = useMemo(
    () => computeCounterValue({ direction, targetDate }),
    [direction, targetDate],
  );

  return (
    <View style={{ padding: spacing.lg, gap: spacing.lg }}>
      <View
        style={{
          alignItems: "center",
          gap: spacing.xxs,
          paddingVertical: spacing.md,
        }}
      >
        <FigureText
          color={color === "red" ? colors.accent : colors.ink}
          style={{ fontSize: 72, lineHeight: 76 }}
        >
          {formatDayCount(preview.days)}
        </FigureText>
        <CaptionText
          color={preview.isPending ? colors.accent : colors.muted}
          style={{ textTransform: "uppercase", letterSpacing: 0.6 }}
        >
          {describeCounterValue(direction, preview)}
        </CaptionText>
      </View>

      <RuleLine />

      <TextField
        label="Label (optional)"
        placeholder="e.g. Since we met"
        value={label}
        onChangeText={setLabel}
        maxLength={60}
        returnKeyType="done"
      />

      <DateField label="Date" value={targetDate} onChange={setTargetDate} />

      <View style={{ gap: spacing.xxs }}>
        <CaptionText
          color={colors.muted}
          style={{ textTransform: "uppercase", letterSpacing: 0.6 }}
        >
          Mode
        </CaptionText>
        <SegmentedControl
          value={direction}
          onChange={setDirection}
          options={[
            { value: "up", label: "Since" },
            { value: "down", label: "Until" },
          ]}
        />
      </View>

      <View style={{ gap: spacing.xxs }}>
        <CaptionText
          color={colors.muted}
          style={{ textTransform: "uppercase", letterSpacing: 0.6 }}
        >
          Colour
        </CaptionText>
        <SegmentedControl
          value={color}
          onChange={setColor}
          options={[
            { value: "ink", label: "Ink" },
            { value: "red", label: "Orange" },
          ]}
        />
      </View>

      <Button
        label={submitting ? "Saving…" : submitLabel}
        disabled={submitting}
        fullWidth
        onPress={async () => {
          setSubmitting(true);
          try {
            await onSubmit({
              label: label.trim(),
              targetDate,
              direction,
              color,
            });
          } finally {
            setSubmitting(false);
          }
        }}
      />

      {onDelete ? (
        <Button
          label="Delete"
          variant="danger"
          fullWidth
          onPress={async () => {
            setSubmitting(true);
            try {
              await onDelete();
            } finally {
              setSubmitting(false);
            }
          }}
        />
      ) : null}
    </View>
  );
}
